import { Component, Input, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe, NgForOf, NgIf } from '@angular/common';
import { QrCodeModule } from 'ng-qrcode';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { NavbarComponent } from '../../../layout/components/navbar/navbar.component';
import { EnhancedPdfExporter } from '../../../../shared/utils/pdfExport';

export interface RiskData {
    reinsuranceSlipNumber: string;
    reinsuranceInsuredCustomerCompanyName: string;
    reinsuranceTypeExtentCoverGranted: string;
    reinsurancePolicyNumber: string;
    reinsuranceSituationTerritorialScope: string;
    reinsuranceCommission: string;
    reinsuranceAggregatePerClass: string;
    reinsuranceRiskPremiumAmount: number;
    reinsuranceStateDateOfReinsurance: string | Date;
    reinsuranceEndDateOfReinsurance: string | Date;
    reinsuranceTransactionDate: string | Date;
    reinsuranceTotalSumInsured: number;
    reinsuranceFacultativeOffer: number;
    reinsuranceLimitOfLiability: number;
    reinsuranceGrossRetention: string;
    reinsuranceNetRetention: string;
}

export interface OfferData {
    reinsuranceSlipNumber: string;
    transactionDate: string | Date;
    client: string;
    reinsurer: string;
    limitOfLiability: number;
    facultativeOffer: number;
    premium: number;
    grossRetention: number;
    netRetention: number;
    offerPercentage: string;
}

@Component({
  selector: 'app-view-negative-market-slip',
  standalone: true,
  imports: [DatePipe, DecimalPipe, NgForOf, QrCodeModule, NavbarComponent, NgIf],
  templateUrl: './view-negative-market-slip.component.html',
  styleUrl: './view-negative-market-slip.component.scss',
})
export class ViewNegativeMarketSlipComponent implements OnInit {
  @Input() riskData: RiskData | any = {
    reinsuranceSlipNumber: '',
    reinsuranceInsuredCustomerCompanyName: '',
    reinsuranceTypeExtentCoverGranted: '',
    reinsurancePolicyNumber: '',
    reinsuranceSituationTerritorialScope: '',
    reinsuranceCommission: '',
    reinsuranceAggregatePerClass: '',
    reinsuranceRiskPremiumAmount: 0,
    reinsuranceStateDateOfReinsurance: '',
    reinsuranceEndDateOfReinsurance: '',
    reinsuranceTransactionDate: '',
    reinsuranceTotalSumInsured: 0,
    reinsuranceFacultativeOffer: 0,
    reinsuranceLimitOfLiability: 0,
    reinsuranceGrossRetention: '',
    reinsuranceNetRetention: '',
  };

  @Input() offersData: OfferData[] = [];

  currentDate: Date = new Date();

  constructor() {}

  ngOnInit(): void {
    // Initialize component
    this.currentDate = new Date();
  }

  // Method to trigger print
  printReport(): void {
    window.print();
  }

  // Method to export as PDF (requires additional library like jsPDF)
  exportAsPDFTwo(): void {
    // Implementation depends on your PDF library choice
    // Example with jsPDF:
    const element: any = document.getElementById('print-container');
    const pdf = new jsPDF();
    pdf.html(element, {
      callback: function (pdf: any) {
        pdf.save('insurance-risk-report.pdf');
      },
    });
  }

  exportAsPDF() {
    // const element:any = document.getElementById('print-container');
    const element: HTMLElement | any = document.querySelector('.print-container');
    // if (!element) return;

    html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save('styled-report.pdf');
    });
  }

  exportProgress: number = 0;
    isExporting: boolean = false;

  async exportToPdf() {
    try {
        this.isExporting = true;
      await EnhancedPdfExporter.exportElementToPdf('.print-container', {
        filename: 'my-report.pdf',
        margin: 15,
        quality: 0.95,
        onProgress: (progress) => {
          // Update your progress bar
          this.exportProgress = progress;
          if(progress >= 100) {
            this.isExporting = false; // Reset exporting state when done
          }
        },
      });
    } catch (error) {
      console.error('Export failed:', error);
      // Handle error in UI
    }
  }

    // Advanced usage with custom options
     async  exportWithCustomOptions(): Promise<void> {
        const element = document.querySelector('.print-container') as HTMLElement;
        if (!element) return;

        const optimalDimensions = EnhancedPdfExporter.getOptimalPdfDimensions(element);
         this.isExporting = true;
        await EnhancedPdfExporter.exportElementToPdf(element, {
            filename: 'custom-report.pdf',
            format: optimalDimensions.format,
            orientation: optimalDimensions.orientation,
            margin: 12,
            scale: 3,
            quality: 0.98,
            backgroundColor: '#f8f9fa',
            onProgress: (progress) => {
                // Update progress bar in UI
                console.log(`Export progress: ${progress}%`);
                if(progress >= 100) {
                    this.isExporting = false; // Reset exporting state when done
                }
            },
            onError: (error) => {
                // Handle errors gracefully
                console.error('PDF export failed:', error);
                // Show user-friendly error message
            }
        });
    }

  // Method to validate data before rendering
  isValidData(): boolean {
    return this.riskData.reinsuranceSlipNumber !== '' && this.offersData.length > 0;
  }

  // Method to get total offers count
  getTotalOffersCount(): number {
    return this.offersData.length;
  }

  // Method to get total premium amount from all offers
  getTotalPremiumAmount(): number {
    return this.offersData.reduce((total, offer) => total + offer.premium, 0);
  }

  // Method to get total facultative offer amount
  baseUrl: string = 'https://reinsurance-portal.smathub.com/'; // Replace with your API base URL
  currentYear: string = new Date().getFullYear().toString();
  getTotalFacultativeOfferAmount(): number {
    return this.offersData.reduce((total, offer) => total + offer.facultativeOffer, 0);
  }
}
